import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiUser, FiLogOut, FiEdit2, FiChevronDown } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../hooks/useAppStore";
import {
  adminLogout,
  selectAdminUser,
  selectAdminProfile,
  fetchAdminMe,
} from "../../store/slices/adminAuthSlice";
import { useToast } from "../../hooks/useToast";

export const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const admin = useAppSelector(selectAdminUser);
  const adminProfile = useAppSelector(selectAdminProfile);
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (admin && !adminProfile) {
      dispatch(fetchAdminMe());
    }
  }, [admin, adminProfile, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await dispatch(adminLogout());
    toast("Signed out", "success");
    navigate("/login");
  };

  if (!admin) return null;

  const firstName = adminProfile?.firstName;
  const lastName = adminProfile?.lastName;
  const fullName =
    firstName || lastName
      ? [firstName, lastName].filter(Boolean).join(" ")
      : admin.email.split("@")[0];
  const photoUrl = adminProfile?.profilePhoto;

  return (
    <header className="h-14 bg-[#1a1818] border-b border-white/10 flex items-center justify-end px-6 flex-shrink-0">
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-white/5 transition-all"
        >
          <div className="w-8 h-8 rounded-full overflow-hidden bg-magenta/20 border border-magenta/30 flex items-center justify-center flex-shrink-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={fullName}
                className="w-full h-full object-cover"
              />
            ) : (
              <FiUser className="w-4 h-4 text-magenta" />
            )}
          </div>
          <div className="text-left">
            <p className="text-offwhite text-sm font-semibold leading-tight">
              {fullName}
            </p>
            <p className="text-offwhite/40 text-xs leading-tight truncate max-w-[140px]">
              {admin.email}
            </p>
          </div>
          <FiChevronDown
            className={`w-4 h-4 text-offwhite/40 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-1.5 w-56 bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-xl overflow-hidden z-50">
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-offwhite text-sm font-semibold truncate">
                {fullName}
              </p>
              <p className="text-offwhite/40 text-xs truncate">{admin.email}</p>
            </div>
            <button
              onClick={() => {
                navigate("/admin-profile");
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-offwhite/70 hover:text-offwhite hover:bg-white/5 transition-colors"
            >
              <FiEdit2 className="w-4 h-4" /> Edit Profile
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400/80 hover:text-red-400 hover:bg-red-400/10 transition-colors"
            >
              <FiLogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};
